// Utilidad compartida para subir fotos de productos al endpoint upload_photo.
// Comprime la imagen a 800px de ancho preservando proporciones y la sube al backend.

import { webserver } from "@/services/webserver.js";

// Dominio unico desde el que se sirven las fotos (proxy a Cloudflare R2).
// Si algun dia cambia el proveedor, este es el unico sitio que se toca.
const PHOTO_BASE_URL = "https://erp.sw67.ca/photos";

/**
 * Cuenta usada como primer nivel de la ruta de fotos: los 6 primeros digitos
 * del store_id. Debe coincidir con el `substr($store, 0, 6)` del backend.
 * @returns {string}
 */
export function photoAccount() {
  const storeId = (window.store && window.store.store_id) || "";
  return String(storeId).substring(0, 6);
}

// Una foto se reemplaza en la MISMA ruta, y Cloudflare obliga a cachearla en el
// navegador varias horas. Sin variar la URL, quien acaba de subir una foto sigue
// viendo la anterior. Aqui se recuerda que ruta cambio en esta sesion para
// añadirle un sufijo y forzar la recarga.
const photoVersions = Object.create(null);

const relativePhotoPath = (marca, refe) => {
  const account = photoAccount();
  if (!marca || !refe || !account) return "";
  return `${account}/${marca}/${String(refe).toLowerCase()}.jpg`;
};

/** Marca la foto de un producto como recien cambiada. */
export function markPhotoUpdated(marca, refe) {
  const path = relativePhotoPath(marca, refe);
  if (path) photoVersions[path] = Date.now();
}

/**
 * URL publica de la foto de un producto.
 * @param {string} marca
 * @param {string} refe
 * @returns {string} URL, o "" si faltan datos
 */
export function photoUrl(marca, refe) {
  const path = relativePhotoPath(marca, refe);
  if (!path) return "";
  const version = photoVersions[path];
  return `${PHOTO_BASE_URL}/${path}${version ? `?v=${version}` : ""}`;
}

/**
 * Variante para quien ya tiene la cuenta y la ruta relativa ("marca/ref.jpg").
 * @param {string} account
 * @param {string} path
 * @returns {string}
 */
export function photoPath(account, path) {
  if (!account || !path) return "";
  return `${PHOTO_BASE_URL}/${account}/${path}`;
}

// Lado mayor de la imagen almacenada y calidad JPEG.
// Sin calidad explicita el navegador usa 0.92, que pesa bastante mas sin verse mejor.
export const PHOTO_MAX_SIZE = 800;
export const PHOTO_QUALITY = 0.82;

// Ajustes neutros: 100 = sin cambio, igual que los porcentajes de CSS filter.
export const NEUTRAL_ADJUSTMENTS = { brightness: 100, contrast: 100, saturation: 100 };

const isNeutral = (a) =>
  !a ||
  ((a.brightness === undefined || a.brightness === 100) &&
    (a.contrast === undefined || a.contrast === 100) &&
    (a.saturation === undefined || a.saturation === 100));

/**
 * Cadena para `ctx.filter` / `style.filter` a partir de los ajustes.
 * @param {{brightness?:number, contrast?:number, saturation?:number}} adjustments
 * @returns {string} "none" si no hay nada que aplicar
 */
export function filterString(adjustments) {
  if (isNeutral(adjustments)) return "none";
  const a = { ...NEUTRAL_ADJUSTMENTS, ...adjustments };
  return `brightness(${a.brightness}%) contrast(${a.contrast}%) saturate(${a.saturation}%)`;
}

/**
 * Carga un File como Image ya decodificada, para poder re-renderizarla varias
 * veces (p.ej. al mover los sliders) sin volver a leer el archivo.
 * @param {File} file
 * @returns {Promise<HTMLImageElement>}
 */
export function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    if (!file) return reject(new Error("No se proporciono archivo"));
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("Error al cargar la imagen"));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error("Error al leer el archivo"));
    reader.readAsDataURL(file);
  });
}

/**
 * Redimensiona limitando el LADO MAYOR (no solo el ancho, que deformaba las
 * verticales) y hornea los ajustes en los pixeles antes de exportar.
 * @param {HTMLImageElement} img
 * @param {Object} [opts]
 * @param {number} [opts.maxSize=800]
 * @param {number} [opts.quality=0.82]
 * @param {Object} [opts.adjustments]
 * @returns {string} data URL (image/jpeg)
 */
export function renderImageToDataUrl(img, opts = {}) {
  const maxSize = opts.maxSize || PHOTO_MAX_SIZE;
  const quality = opts.quality || PHOTO_QUALITY;
  const longest = Math.max(img.width, img.height);
  const ratio = longest > maxSize ? maxSize / longest : 1;

  const canvas = document.createElement("canvas");
  canvas.width = Math.round(img.width * ratio);
  canvas.height = Math.round(img.height * ratio);

  const ctx = canvas.getContext("2d");
  // Fondo blanco: el JPEG no guarda transparencia y sin esto sale en negro
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const filter = filterString(opts.adjustments);
  if (filter !== "none") ctx.filter = filter;
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

  return canvas.toDataURL("image/jpeg", quality);
}

/**
 * Comprime un File de imagen a JPEG limitando el lado mayor a `maxSize` px.
 * @param {File} file
 * @param {number} [maxSize=800]
 * @param {Object} [opts] { quality, adjustments }
 * @returns {Promise<string>} data URL (image/jpeg) de la imagen comprimida
 */
export async function compressImage(file, maxSize = PHOTO_MAX_SIZE, opts = {}) {
  const img = await loadImageFromFile(file);
  return renderImageToDataUrl(img, { ...opts, maxSize });
}

/**
 * Sube una foto al endpoint upload_photo.
 * @param {Object} params
 * @param {string} params.store  store_id
 * @param {string} params.marca  carpeta destino (codigo de marca)
 * @param {string} params.picture nombre de archivo destino (ej. "ABC123.jpg")
 * @param {string} params.photoDataUrl data URL del archivo (JPEG/PNG)
 * @returns {Promise<Object>} respuesta del backend
 */
export function uploadPhoto({ store, marca, picture, photoDataUrl }) {
  return new Promise((resolve, reject) => {
    const qry = {
      store,
      photo: photoDataUrl,
      marca,
      picture,
    };
    webserver(
      "upload_photo",
      qry,
      (response) => resolve(response),
      (err) => reject(err)
    );
  });
}

const assertUploadable = (item, store) => {
  if (!item || !store) throw new Error("item y store son requeridos");
  if (!item.marca || !item.refe) {
    throw new Error("El producto debe tener marca y refe para subir la foto");
  }
};

/**
 * Sube una imagen ya renderizada (p.ej. la vista previa con ajustes aplicados),
 * sin volver a comprimirla. El nombre destino se deriva de `refe`.
 * @param {Object} params
 * @param {string} params.photoDataUrl
 * @param {Object} params.item objeto del producto (usa marca y refe)
 * @param {string} params.store store_id
 * @returns {Promise<{photoDataUrl: string, picture: string, response: any}>}
 */
export async function uploadProductPhotoDataUrl({ photoDataUrl, item, store }) {
  assertUploadable(item, store);
  if (!photoDataUrl) throw new Error("No hay imagen para subir");
  const picture = item.refe.toLowerCase() + ".jpg";
  const response = await uploadPhoto({ store, marca: item.marca, picture, photoDataUrl });
  // La ruta no cambia, asi que hay que invalidar la version cacheada
  markPhotoUpdated(item.marca, item.refe);
  return { photoDataUrl, picture, response };
}

/**
 * Pipeline completo: comprime y sube. El nombre destino se deriva de `refe`.
 * @param {Object} params
 * @param {File} params.file
 * @param {Object} params.item objeto del producto (usa marca y refe)
 * @param {string} params.store store_id
 * @param {number} [params.maxSize=800] lado mayor
 * @param {number} [params.quality=0.82]
 * @param {Object} [params.adjustments] { brightness, contrast, saturation }
 * @returns {Promise<{photoDataUrl: string, picture: string, response: any}>}
 */
export async function compressAndUploadProductPhoto({
  file,
  item,
  store,
  maxSize = PHOTO_MAX_SIZE,
  quality = PHOTO_QUALITY,
  adjustments,
}) {
  if (!file) throw new Error("file es requerido");
  assertUploadable(item, store);
  const photoDataUrl = await compressImage(file, maxSize, { quality, adjustments });
  return uploadProductPhotoDataUrl({ photoDataUrl, item, store });
}
